import React, { useState } from 'react';
import {
  Table, Input, Button, Select, DatePicker, Card, Statistic, Divider, message,
} from 'antd';
import { SearchOutlined, FileExcelOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/bn';
import PropTypes from 'prop-types';
import NavigationLinksHr from './NavLinkHr';

// Set Bengali locale for date display
dayjs.locale('bn');

const { Option } = Select;
const { RangePicker } = DatePicker;

function PaymentStatus({ status }) {
  const getStatusStyle = () => {
    switch (status) {
      case 'paid':
        return 'text-green-500 font-medium';
      case 'pending':
        return 'text-yellow-500 font-medium';
      default:
        return 'text-red-500 font-medium';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'paid':
        return 'প্রদত্ত';
      case 'pending':
        return 'বকেয়া';
      default:
        return 'বাতিল';
    }
  };

  return <span className={getStatusStyle()}>{getStatusText()}</span>;
}

PaymentStatus.propTypes = {
  status: PropTypes.string.isRequired,
};

function CurrencyDisplay({ value, bold = false }) {
  const amount = value.toLocaleString('bn-BD');
  return (
    <span className={bold ? 'font-semibold' : ''}>
      ৳
      {amount}
    </span>
  );
}

CurrencyDisplay.propTypes = {
  value: PropTypes.number.isRequired,
  bold: PropTypes.bool,
};

CurrencyDisplay.defaultProps = {
  bold: false,
};

// Employee name component
function EmployeeName({ name }) {
  return <span className="font-medium">{name}</span>;
}

EmployeeName.propTypes = {
  name: PropTypes.string.isRequired,
};

// Payment date component
const PaymentDate = ({ date }) => (date ? dayjs(date).format('DD MMM, YYYY') : '-');

// Summary row component
function SummaryRow({ filteredData }) {
  const totalEarnings = filteredData.reduce((sum, item) => sum + item.totalEarnings, 0);
  const totalDeductions = filteredData.reduce((sum, item) => sum + item.totalDeductions, 0);
  const totalNetSalary = filteredData.reduce((sum, item) => sum + item.netSalary, 0);

  return (
    <Table.Summary.Row className="font-bold bg-gray-50">
      <Table.Summary.Cell index={0} colSpan={4}>মোট</Table.Summary.Cell>
      <Table.Summary.Cell index={1}>
        <CurrencyDisplay value={totalEarnings} />
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2}>
        <CurrencyDisplay value={totalDeductions} />
      </Table.Summary.Cell>
      <Table.Summary.Cell index={3}>
        <CurrencyDisplay value={totalNetSalary} bold />
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} colSpan={3} />
    </Table.Summary.Row>
  );
}

SummaryRow.propTypes = {
  filteredData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

// Mock data for Bangladeshi payroll
const mockPayrollData = [
  {
    key: '1',
    employeeId: 'EMP-1001',
    name: 'আহমেদ রফিক',
    designation: 'সেলস ',
    department: 'বিক্রয়',
    basicSalary: 35000,
    houseRent: 17500,
    medicalAllowance: 2000,
    transportAllowance: 1500,
    overtime: 4500,
    bonus: 5000,
    pfContribution: 3500,
    tax: 1250,
    advance: 0,
    totalEarnings: 65000,
    totalDeductions: 4750,
    netSalary: 60250,
    paymentStatus: 'paid',
    paymentDate: '2023-06-28',
    accountNumber: '1234567890123',
    bank: 'ডাচ-বাংলা ব্যাংক',
  },
  // ... (other mock data entries remain the same)
];

function Payroll() {
  const [searchText, setSearchText] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const columns = [
    {
      title: 'কর্মচারী আইডি',
      dataIndex: 'employeeId',
      key: 'employeeId',
      sorter: (a, b) => a.employeeId.localeCompare(b.employeeId),
    },
    {
      title: 'নাম',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <EmployeeName name={text} />,
    },
    {
      title: 'পদবী',
      dataIndex: 'designation',
      key: 'designation',
    },
    {
      title: 'বিভাগ',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'মোট আয়',
      dataIndex: 'totalEarnings',
      key: 'totalEarnings',
      render: (text) => <CurrencyDisplay value={text} />,
      sorter: (a, b) => a.totalEarnings - b.totalEarnings,
    },
    {
      title: 'মোট কর্তন',
      dataIndex: 'totalDeductions',
      key: 'totalDeductions',
      render: (text) => <CurrencyDisplay value={text} />,
    },
    {
      title: 'নিট বেতন',
      dataIndex: 'netSalary',
      key: 'netSalary',
      render: (text) => <CurrencyDisplay value={text} bold />,
      sorter: (a, b) => a.netSalary - b.netSalary,
    },
    {
      title: 'স্ট্যাটাস',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (text) => <PaymentStatus status={text} />,
      filters: [
        { text: 'প্রদত্ত', value: 'paid' },
        { text: 'বকেয়া', value: 'pending' },
        { text: 'বাতিল', value: 'cancelled' },
      ],
      onFilter: (value, record) => record.paymentStatus === value,
    },
    {
      title: 'প্রদানের তারিখ',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (text) => <PaymentDate date={text} />,
    },
    {
      title: 'ব্যাংক',
      dataIndex: 'bank',
      key: 'bank',
    },
  ];

  const filteredData = mockPayrollData.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchText.toLowerCase())
      || item.employeeId.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus = paymentStatusFilter === 'all'
      || item.paymentStatus === paymentStatusFilter;

    const matchesDate = dateRange.length === 0 || (
      (!item.paymentDate || dayjs(item.paymentDate).isBetween(dateRange[0], dateRange[1], null, '[]'))
    );

    return matchesSearch && matchesStatus && matchesDate;
  });

  const rowSelection = {
    onChange: (_, selectedRows) => {
      setSelectedRows(selectedRows);
    },
  };

  const handleProcessPayment = () => {
    if (selectedRows.length === 0) {
      message.warning('দয়া করে বেতন প্রক্রিয়া করার জন্য কর্মচারী নির্বাচন করুন');
      return;
    }
    message.success(`${selectedRows.length} জন কর্মচারীর বেতন প্রক্রিয়া করা হয়েছে`);
  };

  const handleExport = () => {
    message.success('এক্সেল ফাইল হিসেবে ডেটা ডাউনলোড করা হয়েছে');
  };

  const handlePrint = () => {
    message.info('প্রিন্ট প্রস্তুত করা হচ্ছে...');
  };

  const totalPayout = filteredData.reduce((sum, item) => sum + item.netSalary, 0);
  const paidCount = filteredData.filter((item) => item.paymentStatus === 'paid').length;
  const pendingCount = filteredData.filter((item) => item.paymentStatus === 'pending').length;

  // Define summary function outside of render
  const tableSummary = () => (
    <Table.Summary fixed>
      <SummaryRow filteredData={filteredData} />
    </Table.Summary>
  );

  return (
    <div className="p-5">
      <NavigationLinksHr />
      <h1 className="text-blue-500 mb-6 text-2xl font-semibold">
        বেতন ব্যবস্থাপনা
      </h1>

      <div className="flex flex-wrap gap-4 mb-6">
        <Card className="flex-1 min-w-[200px]">
          <Statistic
            title="মোট বেতন প্রদান"
            value={totalPayout}
            precision={2}
            valueStyle={{ color: '#3f8600' }}
            prefix="৳"
            suffix="টাকা"
            formatter={(value) => value.toLocaleString('bn-BD')}
          />
        </Card>
        <Card className="flex-1 min-w-[200px]">
          <Statistic
            title="প্রদত্ত বেতন"
            value={paidCount}
            suffix={`/ ${filteredData.length}`}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
        <Card className="flex-1 min-w-[200px]">
          <Statistic
            title="বকেয়া বেতন"
            value={pendingCount}
            valueStyle={{ color: '#faad14' }}
          />
        </Card>
      </div>

      <div className="flex flex-wrap gap-4 mb-5">
        <Input
          placeholder="কর্মচারী আইডি বা নাম অনুসন্ধান করুন"
          prefix={<SearchOutlined />}
          className="w-full md:w-80"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Select
          placeholder="পেমেন্ট স্ট্যাটাস"
          className="w-full md:w-48"
          value={paymentStatusFilter}
          onChange={setPaymentStatusFilter}
        >
          <Option value="all">সব স্ট্যাটাস</Option>
          <Option value="paid">প্রদত্ত</Option>
          <Option value="pending">বকেয়া</Option>
          <Option value="cancelled">বাতিল</Option>
        </Select>

        <RangePicker
          className="w-full md:w-72"
          placeholder={['শুরুর তারিখ', 'শেষ তারিখ']}
          onChange={setDateRange}
          format="DD MMM, YYYY"
        />

        <div className="flex-1 flex justify-end gap-2">
          <Button
            type="primary"
            onClick={handleProcessPayment}
            disabled={selectedRows.length === 0}
          >
            বেতন প্রক্রিয়া করুন
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>
            এক্সেল
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            প্রিন্ট
          </Button>
        </div>
      </div>

      <Divider />

      <Table
        columns={columns}
        dataSource={filteredData}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        scroll={{ x: true }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `মোট ${total} জন কর্মচারী`,
        }}
        bordered
        summary={tableSummary}
      />
    </div>
  );
}

export default Payroll;
